// src/utils/thumbnailGenerator.js
// Client-side thumbnail generation to avoid backend memory issues

/**
 * Generate a thumbnail for a slide using Canvas API
 * @param {Object} slide - Slide data with text_content, colors, fonts, etc.
 * @param {Object} options - Configuration options
 * @returns {Promise<string>} Base64 encoded JPEG thumbnail
 */
export async function generateSlideThumbnail(slide, options = {}) {
  const {
    width = 400,
    height = 300,
    backgroundColor = '#ffffff',
    textColor = '#000000',
    quality = 0.8
  } = options;

  return new Promise((resolve, reject) => {
    try {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Extract slide data
      const textContent = slide.text_content || '';
      const slideColors = slide.colors || [];
      const slideFonts = slide.fonts || [];
      const slideNumber = slide.slide_number || 1;

      // Use slide's first color as background if available
      const bgColor = slideColors[0] || backgroundColor;
      const fgColor = slideColors[1] || textColor;

      // Draw background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, width, height);

      // Add subtle gradient for depth
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, bgColor);
      gradient.addColorStop(1, adjustBrightness(bgColor, -10));
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Draw border
      ctx.strokeStyle = adjustBrightness(bgColor, -30);
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, width, height);

      // Set text color (ensure contrast)
      ctx.fillStyle = ensureContrast(fgColor, bgColor);

      // Extract title (first line or first sentence)
      const lines = textContent.split('\n').filter(line => line.trim());
      const title = lines[0] || `Slide ${slideNumber}`;

      // Draw title
      const titleFont = slideFonts[0] || 'Arial';
      ctx.font = `bold 28px ${titleFont}, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      // Word wrap title
      const titleLines = wrapText(ctx, title, width - 40, 3);
      let yPos = 30;
      titleLines.forEach(line => {
        ctx.fillText(line, 20, yPos);
        yPos += 36;
      });

      // Draw body text
      if (lines.length > 1) {
        ctx.font = `16px ${titleFont}, Arial, sans-serif`;
        ctx.fillStyle = adjustOpacity(ensureContrast(fgColor, bgColor), 0.8);
        yPos += 20;

        const bodyLines = lines.slice(1, 6); // Show up to 5 more lines
        bodyLines.forEach((line, idx) => {
          const wrappedLines = wrapText(ctx, line, width - 40, 1);
          wrappedLines.forEach(wrapped => {
            if (yPos < height - 40) {
              ctx.fillText(wrapped, 20, yPos);
              yPos += 22;
            }
          });
        });
      }

      // Draw slide number badge
      ctx.fillStyle = adjustBrightness(bgColor, -40);
      ctx.fillRect(width - 60, height - 40, 50, 30);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${slideNumber}`, width - 35, height - 20);

      // Convert to base64
      const thumbnail = canvas.toDataURL('image/jpeg', quality);
      resolve(thumbnail);
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      // Return fallback placeholder
      resolve(generatePlaceholder(slide.slide_number));
    }
  });
}

/**
 * Wrap text to fit within a given width
 */
function wrapText(ctx, text, maxWidth, maxLines = 999) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (let i = 0; i < words.length && lines.length < maxLines; i++) {
    const testLine = currentLine + words[i] + ' ';
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && currentLine !== '') {
      lines.push(currentLine.trim());
      currentLine = words[i] + ' ';
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine.trim() !== '' && lines.length < maxLines) {
    lines.push(currentLine.trim());
  }

  return lines;
}

/**
 * Adjust color brightness
 */
function adjustBrightness(color, percent) {
  // Parse hex color
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  let r = parseInt(hex.substr(0, 2), 16);
  let g = parseInt(hex.substr(2, 2), 16);
  let b = parseInt(hex.substr(4, 2), 16);

  r = Math.max(0, Math.min(255, r + (r * percent / 100)));
  g = Math.max(0, Math.min(255, g + (g * percent / 100)));
  b = Math.max(0, Math.min(255, b + (b * percent / 100)));

  const toHex = (n) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Adjust color opacity
 */
function adjustOpacity(color, opacity) {
  // Convert hex to rgba
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Ensure text color has good contrast with background
 */
function ensureContrast(textColor, bgColor) {
  const brightness = (color) => {
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  const bgBrightness = brightness(bgColor);

  // If background is light, use dark text
  // If background is dark, use light text
  return bgBrightness > 128 ? '#000000' : '#ffffff';
}

/**
 * Generate a simple placeholder thumbnail
 */
function generatePlaceholder(slideNumber = 1) {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 300;
  const ctx = canvas.getContext('2d');

  // Gradient background
  const gradient = ctx.createLinearGradient(0, 0, 400, 300);
  gradient.addColorStop(0, '#4a5568');
  gradient.addColorStop(1, '#2d3748');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 300);

  // Text
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 48px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`Slide ${slideNumber}`, 200, 150);

  return canvas.toDataURL('image/jpeg', 0.8);
}

/**
 * Upload thumbnail to Supabase Storage
 * @param {string} base64Image - Base64 encoded image
 * @param {string} projectId - Project ID
 * @param {string} slideNumber - Slide number
 * @returns {Promise<string|null>} Public URL or null
 */
export async function uploadThumbnailToSupabase(base64Image, projectId, slideNumber = 1) {
  try {
    const { supabaseHelpers } = await import('../lib/supabase');

    // Convert base64 to blob
    const base64Data = base64Image.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });

    // Upload to Supabase
    const fileName = `thumbnails/${projectId}/slide_${slideNumber}.jpg`;
    const { data, error } = await supabaseHelpers.uploadFile('thumbnails', fileName, blob);

    if (error) {
      console.error('Error uploading thumbnail to Supabase:', error);
      return null;
    }

    // Get public URL
    const { data: { publicUrl } } = supabaseHelpers.getPublicUrl('thumbnails', fileName);

    return publicUrl;
  } catch (error) {
    console.error('Error in uploadThumbnailToSupabase:', error);
    return null;
  }
}

export default {
  generateSlideThumbnail,
  uploadThumbnailToSupabase
};
